import {
  Button,
  CIActionGuard,
  CIAppliedFilter,
  CIAppliedListFilter,
  CIPageHeader,
  Loader,
  Modal,
  NoDataAvailable,
  SearchInput,
  toLocalDate,
  toUtcDate,
  UnableToFetch,
  Unathourized,
  useToastMessage,
  useUserPermission,
  usePaginationPreference
} from 'ccs-ui-components';
import _ from 'lodash';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import { requiredAuthPermission } from '../../../common/constants/UserPermissionConstants';
import { exportJourneyConfiguration, getAllJourneys, getListOfJourneysForParticularJourney } from '../../store/_api';
import JourneyListFilter from '../../_components/journey-list-filter/JourneyListFilter';
import { journeyBuilderRoutes } from '../../constants/JdRoutes';
import { clearImportJourneySummary } from '../../store/reducer/ImportJourneySummaryReducer';
import ImportJourney from '../import-journeys/ImportJourney';
import { arrayBufferToString } from '../journey-canvas/utility/helpers';
import { ApiModifySort, DataLoadingState, defaultJourneyListStatus, defaultJourneyListStatusArray, exportConfigConst, getFilterParams, journeyListConst, JourneyListState, JourneyStatus, Labels } from './Utility';
import { JourneyListFilterData } from '../../_components/journey-list-filter/Utils';
import ListJourneys from '../../_components/journey-list/ListJourneys';
import { IRecordModel } from '../../store/_api/TypeDefs';
import { AppDispatch, RootState } from '../../store/AppStore';
import classes from './JourneyList.module.scss';
import { addFilterJourneyData, addSortJourneyData } from './JourneyListFilterReducer';

const JourneyList = () => {
  const { searchText, status, fromDate, toDate, sort } = useSelector((state: RootState) => state.filterJourneyReducer);

  // state variables
  const [journeyList, setJourneyList] = useState<JourneyListState[]>([]);
  const [resStatusCode, setResStatusCode] = useState<number>();
  const [resError, setResError] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [listLength, setListLength] = useState<number>(0);

  const { getPreferredPageSize, persistPageSize } = usePaginationPreference();
  const [item, setItem] = useState<number>(getPreferredPageSize());

  const [searchTemp, setSearchTemp] = useState<string>();
  const [searchJourney, setSearchJourney] = useState<string>();
  const [journeyNameSort, setJourneyNameSort] = useState<string>();
  const [statusSort, setStatusSort] = useState<string>();
  const [updatedBySort, setUpdatedBySort] = useState<string>();
  const [sortParameter, setSortParameter] = useState<string>();
  const [filterData, setFilterData] = useState<JourneyListFilterData>();
  const [apiResponseStatus, setApiResponseStatus] = useState<DataLoadingState>(
    DataLoadingState.LOADING
  );
  const [lastModifiedSort, setLastModifiedSort] = useState<string>(
    ApiModifySort.DESC
  );

  /**
   * States to manage the export configuration of journeys
   */
  const [isCheckAllJourneys, setIsCheckAllJourneys] = useState(false);
  const [selectedJourneyList, setSelectedJourneyList] = useState<IRecordModel[]>([]);
  const [activeJourneys, setActiveJourneys] = useState<IRecordModel[]>([]);

  const dispatch = useDispatch<AppDispatch>();
  const [showVersionModal, setShowVersionModal] = useState(false);
  const [journeyNameLocal, setJourneyNameLocal] = useState<string>();
  const [mode, setMode] = useState<string>();
  const [selectedJourneyVersion, setSelectedJourneyVersion] = useState<string>();
  const [versionLocal, setVersionLocal] = useState<string>('');
  const navigate = useNavigate();
  const [sortedName, setSortedName] = useState(Labels.LAST_MODIFIED);
  const [sortedOrder, setSortedOrder] = useState(ApiModifySort.DESC);
  const [showModalNotAllowed, setShowModalNotAllowed] = useState<boolean>(false);

  // hooks
  const toastMessage = useToastMessage();
  const history = useNavigate();
  const route = useLocation();
  const { permissionObject } = useUserPermission();
  const { t: translate } = useTranslation();

  const params = useMemo(
    () =>
      getFilterParams(route.search, { searchText, status, fromDate, toDate, sort }),
    [route.search, searchText, status, fromDate, toDate, sort]
  );

  const [queryParams, setQueryParams] = useState<{ [key: string]: string }>(params);

  // local variables
  const FILTER = {
    TO_TIMESTAMP_EXTENSION_STR: 'T23:59:59',
    FROM_TIMESTAMP_EXTENSION_STR: 'T00:00:00'
  };

  const allStatusList = [
    { value: JourneyStatus.dropdown_active, label: translate('journey_list_component.txt_active') },
    { value: JourneyStatus.dropdown_draft, label: translate('journey_list_component.txt_draft') },
    { value: JourneyStatus.dropdown_inactive, label: translate('journey_list_component.txt_inactive') },
    { value: JourneyStatus.dropdown_returned, label: translate('journey_list_component.txt_returned') },
    { value: JourneyStatus.dropdown_pending_approval_value, label: translate('journey_list_component.txt_panding') }
  ];
  const allStatusMap: { [key: string]: string } = {};
  allStatusList.forEach((s) => { allStatusMap[s.value] = s.label; });
  const debounceDelay = 300;

  /**
   * @method appliedFilterData
   * @description The applied filter data.
   */
  const appliedFilterData: CIAppliedFilter[] = useMemo(() => {
    const data: CIAppliedFilter[] = [];
    if (!filterData) {
      return [];
    }
    if (filterData.status.length > 0 && filterData.status.length < allStatusList.length) {
      filterData.status.forEach((f) => data.push({ text: allStatusMap[f], type: 'status', value: f }));
    }
    if (filterData.startDate && filterData.endDate) {
      data.push({ value: filterData.endDate, type: 'date', text: `${translate('journey_list_component.txt_last_modified')}: ${toLocalDate({ date: filterData.startDate, dateTimeFormat: { date: true } })} - ${toLocalDate({ date: filterData.endDate, dateTimeFormat: { date: true } })}` });
    } else if (filterData.startDate) {
      data.push({ value: filterData.startDate, type: 'start_date', text: `${translate('journey_list_component.txt_last_modified_from')}: ${toLocalDate({ date: filterData.startDate, dateTimeFormat: { date: true } })}` });
    } else if (filterData.endDate) {
      data.push({ value: filterData.endDate, type: 'end_date', text: `${translate('journey_list_component.txt_last_modified_to')}: ${toLocalDate({ date: filterData.endDate, dateTimeFormat: { date: true } })}` });
    }
    return data;
  }, [filterData]);

  const isShowingDefaultFilter = useMemo(
    () => {
      const statuses = filterData?.status ?? [];
      const defaultSet = new Set(defaultJourneyListStatusArray);
      const isDefaultStatuses = statuses.length === defaultJourneyListStatusArray.length && statuses.every((s) => defaultSet.has(s));
      return isDefaultStatuses && !filterData?.startDate && !filterData?.endDate;
    },
    [filterData]
  );

  /**
   * @method createJourney
   * @description takes you to create journey page
   */
  const createJourney = () => {
    history(journeyBuilderRoutes.createJourney);
  };
  /**
   * @method importJourney
   * @description takes you to create journey page
   */
  const importJourney = () => {
    history(journeyBuilderRoutes.importJourney);
    dispatch(clearImportJourneySummary());
  };

  /**
   * @method paginationParam
   * @description sets the pagination parameter
   */
  const paginationParam = () => {
    setItem(queryParams.item ? Number(queryParams.item) : getPreferredPageSize());
    setPage(
      queryParams.page === undefined ? 0 : Number(queryParams.page)
    );
  };

  /**
   * @method setFilterStates
   * @description sets the filter
   */
  const setFilterStates = () => {
    const setLastModifiedSortCondition = !queryParams.sort ? 'DESC' : '';
    setJourneyNameSort(
      queryParams.sort === Labels.JOURNEY_NAME ? queryParams.type : ''
    );
    setStatusSort(queryParams.sort === Labels.JOURNEY_STATUS ? queryParams.type : '');
    setLastModifiedSort(
      queryParams.sort === Labels.LAST_MODIFIED
        ? queryParams.type
        : setLastModifiedSortCondition
    );
    setSortParameter(
      !queryParams.sort
        ? `${Labels.LAST_MODIFIED},${ApiModifySort.DESC}`
        : `${queryParams.sort},${queryParams.type}`
    );
    if (queryParams.journeyName !== undefined) {
      setSearchTemp(queryParams.journeyName);
      setSearchJourney(queryParams.journeyName);
    }
    const filter: JourneyListFilterData = { status: [] };
    if (queryParams.status) {
      filter.status = queryParams.status.split(',');
    }
    paginationParam();
    filter.startDate = queryParams.fromDate;
    filter.endDate = queryParams.toDate;

    dispatch(addFilterJourneyData({
      searchText: queryParams.journeyName ?? '',
      status: queryParams.status ?? '',
      toDate: queryParams.toDate ?? '',
      fromDate: queryParams.fromDate ?? ''
    }));

    const sortForStore = !queryParams.sort
      ? `${Labels.LAST_MODIFIED},${ApiModifySort.DESC}`
      : `${queryParams.sort},${queryParams.type || ApiModifySort.DESC}`;
    dispatch(addSortJourneyData(sortForStore));

    setFilterData(filter);
  };

  /**
   * @method filterHandler
   * @description handles the filter for the journey list page
   */
  const filterHandler = (data: JourneyListFilterData) => {
    setPage(0);

    const newFilters = {
      journeyName: searchJourney ?? '',
      status: data.status?.join(',') ?? '',
      fromDate: data.startDate ?? '',
      toDate: data.endDate ?? '',
      page: '0'
    };

    setQueryParams((pre) => ({
      ...pre,
      ...newFilters
    }));
  };

  /**
   * @method debounceHandler
   * @description handles delay after the filtered journey names
   */
  const debounceHandler = useCallback(
    _.debounce((value: string) => {
      setSearchJourney(value);

      setQueryParams((pre) => ({
        ...pre,
        journeyName: value ?? '',
        page: String(0)
      }));
    }, debounceDelay),
    []
  );

  /**
   * @method searchHandler
   * @description handles the search in the search bar
   * @param {ChangeEvent<HTMLInputElement>} e
   */
  const searchHandler = (e: ChangeEvent<HTMLInputElement>) => {
    setPage(0);
    setSearchTemp(e.target.value);
    debounceHandler(e.target.value);
  };

  /**
   * @method onGetPageHandler
   * @description handles the pages of the list (previous page or next page)
   * @param pageNumber
   */
  const onGetPageHandler = (pageNumber: number) => {
    setPage(pageNumber);
    setQueryParams((pre) => ({
      ...pre,
      page: String(pageNumber)
    }));
  };

  /**
   * @method onGetItemsHandler
   * @description handles the item to show in the page
   * @param itemNumber
   */
  const onGetItemsHandler = (itemNumber: number) => {
    setItem(itemNumber);
    setQueryParams((pre) => ({
      ...pre,
      item: String(itemNumber)
    }));
  };

  const onDeleteSuccess = () => {
    onGetItemsHandler(0);
  };

  /**
   * @method handleSelectAll
   * @description Handles the select all checkbox when Select all active journeys checkbox is selected
   */
  const handleSelectAll = () => {
    setIsCheckAllJourneys(!isCheckAllJourneys);
    const journeys = selectedJourneyList.filter(
      (item) => !activeJourneys.some((journey) => journey.recordName === item.recordName && journey.recordVersion === item.recordVersion)
    );
    if (!isCheckAllJourneys) {
      setSelectedJourneyList(journeys.concat(activeJourneys));
    } else {
      setSelectedJourneyList(journeys);
    }
  };

  /**
   * @method exportJourneyConfigurationHandler
   * @description export the active journey from the list
   */
  const exportJourneyConfigurationHandler = async () => {
    try {
      setApiResponseStatus(DataLoadingState.LOADING);
      const resp = await exportJourneyConfiguration(selectedJourneyList);
      if (resp.data) {
        const contentDisposition = resp.request.getResponseHeader(exportConfigConst.contentDisposition_text);
        const filename = contentDisposition ? contentDisposition.split('filename="')[1].split('.')[0] : 'download';
        const url = window.URL.createObjectURL(
          new Blob([resp.data])
        );
        // Create a temporary download link
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute(exportConfigConst.download_text, `${filename}.zip`);
        link.setAttribute(exportConfigConst.download_text, `${filename}.zip`);

        // Simulate a click event
        const clickEvent = new MouseEvent('click', {
          view: window,
          bubbles: true,
          cancelable: true
        });
        link.dispatchEvent(clickEvent);

        // Clean up the URL object
        window.URL.revokeObjectURL(url);

        toastMessage.success(
          translate('journey_list_component.title_toast_success'),
          translate('journey_list_component.msg_export_journey_config_success')
        );
        setSelectedJourneyList([]);
        setApiResponseStatus(DataLoadingState.COMPLETED);
      }
    } catch (e: unknown) {
      const error = e as { response?: { data: unknown } };
      const responseData = error?.response?.data;
      let data;
      if (error?.response?.data) {
        data = JSON.parse(arrayBufferToString(responseData));
      }
      setApiResponseStatus(DataLoadingState.COMPLETED);
      toastMessage.failure(
        translate('journey_list_component.title_toast_error'),
        data?.errorMessage
      );
    }
  };

  /**
   * @method viewJourney
   * @description View the journey List data in canvas
   * @param journeyName
   * @param currStatus
   * @param versionNum
   */
  const viewJourney = (journeyName: string, versionNum: string) => {
    setJourneyNameLocal(journeyName);
    setMode('view');
    setSelectedJourneyVersion(versionNum);
  };

  /**
   * @method editJourney
   * @description edit the journey list in canvas
   * @param journeyName
   * @param currStatus
   * @param versionNum
   */
  const editJourney = async (journeyName: string, currStatus: string, versionNum: string) => {
    setJourneyNameLocal(journeyName);
    if (currStatus.toLocaleLowerCase() === Labels.JOURNEY_STATUS_ACTIVE || currStatus.toLocaleLowerCase() === Labels.JOURNEY_STATUS_INACTIVE) {
      setApiResponseStatus(DataLoadingState.LOADING);
      const resp = await getListOfJourneysForParticularJourney(journeyName);
      setResError(resp.isError);
      if (resp.isError) {
        setApiResponseStatus(DataLoadingState.FAILED);
      } else {
        const value = resp.journeys.findIndex((item) => (['draft', 'returned', 'pending_approval'].includes(item.status.toLowerCase())));
        if (value === -1) {
          setShowVersionModal(true);
          setJourneyNameLocal(journeyName);
          setVersionLocal(versionNum);
        } else {
          setShowModalNotAllowed(true);
        }
        setApiResponseStatus(DataLoadingState.COMPLETED);
      }
    } else {
      setMode('edit');
      setSelectedJourneyVersion(versionNum);
    }
  };

  const handleCloseVersionModal = () => setShowVersionModal(false);

  /**
   * @method handleSaveVersionModal
   * @description creates a new version for the active journey when clicked edit
   */
  const handleSaveVersionModal = () => {
    setShowVersionModal(false);
    setMode('edit');
    setSelectedJourneyVersion(versionLocal);
  };

  /**
   * @method newVersionModalBody
   * @description The body content for the new version modal.
   */
  const newVersionModalBody = (
    <div data-testid="modal-for-active-inactive">
      {translate('journey_list_component.txt_new_journey_desc')}
    </div>
  );

  /**
   * @method footerOptionsObj
   * @description The options for the footer.
   */
  const footerOptionsObj = {
    itemsPerPage: item,
    currentPageNumber: page,
    numberOfPages: Math.ceil(listLength / item),
    onCurrentPageNumberChange: onGetPageHandler,
    onItemsPerPageChange: onGetItemsHandler
  };

  /**
   * @method footerObj
   * @description The footer object.
   */
  const footerObj = { footerType: 'paging', footerOptions: footerOptionsObj };

  /**
   * @method handleClick
   * @description Handles the click event When individual active journeys checkbox is selected
   * @param {React.ChangeEvent<HTMLInputElement>} name - The event object.
   * @param {number} version - The version number.
  */
  const handleClick = (name: React.ChangeEvent<HTMLInputElement>, version: number) => {
    const { id, checked } = name.target;
    if (checked && process.env.REACT_APP_MAX_EXPORT_JOURNEY && selectedJourneyList.length >= parseInt(process.env.REACT_APP_MAX_EXPORT_JOURNEY, 10)) {
      toastMessage.failure(
        translate('journey_list_component.title_toast_error'),
        translate('journey_list_component.msg_max_export_limit_reached_header', {
          replace: {
            maxLimit: process.env.REACT_APP_MAX_EXPORT_JOURNEY
          }
        })
      );
    } else {
      setSelectedJourneyList([...selectedJourneyList, { recordName: id, recordVersion: version }]);
    }
    if (!checked) {
      setSelectedJourneyList(selectedJourneyList.filter((item) => item.recordName !== id));
    }
  };

  /**
   * @method appliedFilterChangeHandler
   * @description applies the filter changes when apply button is clicked
   * @param filters
   */
  const appliedFilterChangeHandler = (filters: CIAppliedFilter[]) => {
    const statusList: string[] = [];
    let hasStartDate = false;
    let hasEndDate = false;
    filters.forEach((f) => {
      if (f.type === 'status') {
        statusList.push(f.value);
      } else if (f.type === 'start_date') {
        hasStartDate = true;
      } else if (f.type === 'end_date') {
        hasEndDate = true;
      } else if (f.type === 'date') {
        hasStartDate = true;
        hasEndDate = true;
      }
    });
    const data: JourneyListFilterData = {
      status: statusList,
      startDate: hasStartDate ? filterData?.startDate : undefined,
      endDate: hasEndDate ? filterData?.endDate : undefined
    };
    setPage(0);

    const newFilters = {
      journeyName: searchJourney ?? '',
      status: data.status?.join(',') ?? '',
      fromDate: data.startDate ?? '',
      toDate: data.endDate ?? '',
      page: '0'
    };

    setQueryParams((pre) => ({
      ...pre,
      ...newFilters
    }));
  };

  const resetFilterHandler = () => {
    setPage(0);
    setQueryParams((pre) => ({
      ...pre,
      status: defaultJourneyListStatus,
      fromDate: '',
      toDate: '',
      page: '0'
    }));
  };

  useEffect(() => {
    setSearchJourney(queryParams.journeyName ?? '');
    setSearchTemp(queryParams.journeyName ?? '');

    if (queryParams.sort && queryParams.type) {
      setSortParameter(`${queryParams.sort},${queryParams.type}`);
      setSortedName(queryParams.sort);
      setSortedOrder(queryParams.type as ApiModifySort);
    }
  }, [route.search]);

  useEffect(() => {
    if (Object.keys(queryParams).length === 0) {
      setSortParameter(`${Labels.LAST_MODIFIED},${ApiModifySort.DESC}`);
    } else {
      const searchParams = new URLSearchParams();
      Object.entries(queryParams).forEach(([k, v]) => {
        if (v) {
          searchParams.set(k, v);
        }
      });

      setFilterStates();
      const queryString = searchParams.toString();
      history(
        queryString ? `?${queryString}` : route.search,
        { state: route.search }
      );
    }
  }, [queryParams]);

  useEffect(() => {
    if (mode) {
      const pathname = journeyBuilderRoutes.journeyCanvas.replace(':id', journeyNameLocal ?? '').replace(':mode', mode ?? '').replace(':version', selectedJourneyVersion ?? '');
      navigate({
        pathname,
      });
    }
  }, [mode]);

  const filterStartDate = filterData?.startDate
    ? `${filterData?.startDate}${FILTER.FROM_TIMESTAMP_EXTENSION_STR}`
    : undefined;
  const filterEndDate = filterData?.endDate
    ? `${filterData?.endDate}${FILTER.TO_TIMESTAMP_EXTENSION_STR}`
    : undefined;
  const requestFromDate = (filterStartDate || queryParams.fromDate)
    ? toUtcDate({ date: filterStartDate ?? `${queryParams.fromDate}${FILTER.FROM_TIMESTAMP_EXTENSION_STR}` })
    : '';
  const requestToDate = (filterEndDate || queryParams.toDate)
    ? toUtcDate({ date: filterEndDate ?? `${queryParams.toDate}${FILTER.TO_TIMESTAMP_EXTENSION_STR}` })
    : '';

  useEffect(() => {
    if (!sortParameter) {
      return;
    }
    (async () => {
      setApiResponseStatus(DataLoadingState.LOADING);
      const resp = await getAllJourneys({
        page,
        pageSize: item,
        validFromDate: requestFromDate.replace(' ', 'T'),
        validToDate: requestToDate.replace(' ', 'T'),
        searchJourney: searchJourney ?? queryParams.journeyName,
        statusFilter: queryParams.status ? queryParams.status.split(',') : [],
        sortParameter: queryParams.sort ? `${queryParams.sort},${queryParams.type}` : sortParameter,
        validationName: false
      });
      setResError(resp.isError);
      setResStatusCode(resp.statusCode);
      if (resp.isError) {
        setApiResponseStatus(DataLoadingState.FAILED);
        setListLength(0);
      } else {
        setJourneyList(resp.journeys);
        setApiResponseStatus(DataLoadingState.COMPLETED);
        setListLength(resp.totalJourneysCount);
      }
    })();
  }, [
    item,
    page,
    queryParams,
    searchJourney,
    sortParameter,
    requestFromDate,
    requestToDate
  ]);

  useEffect(() => {
    const activeItems = journeyList?.filter(
      (item) => item.status.toLocaleLowerCase() === Labels.JOURNEY_STATUS_ACTIVE
    );
    const value: IRecordModel[] = activeItems?.map((data) => ({
      recordName: data.journeyName,
      recordVersion: data.version,
    }));
    setActiveJourneys(value);
    if (
      value.length
      && value.every((item) => selectedJourneyList.includes(item))
    ) {
      setIsCheckAllJourneys(true);
    } else {
      setIsCheckAllJourneys(false);
    }
  }, [journeyList, page]);

  useEffect(() => {
    if (queryParams.item) {
      persistPageSize(Number(queryParams.item));
    }
  }, [queryParams.item]);

  /**
  * To toggle
  */
  useEffect(() => {
    if (
      activeJourneys.length
      && activeJourneys.every((item) => selectedJourneyList.includes(item))
    ) {
      setIsCheckAllJourneys(true);
    } else {
      setIsCheckAllJourneys(false);
    }
  }, [selectedJourneyList]);

  return (
    <div
      className={classes['side-nav-container']}
      data-testid="journeyListPage"
    >
      {toastMessage.renderToast()}
      <div className={`container ${classes['journey-list-container']}`}>
        <CIPageHeader className={classes['header-wrapper']} header={translate('journey_list_component.title_journeys')} />
        <div className={classes.filters}>
          <div className={classes['search-filter-div']} data-testid="searchFilterRoot">
            <JourneyListFilter data={filterData} onApply={filterHandler} onResetFilters={resetFilterHandler} allStatusList={allStatusList} />
            <div className={classes.search}>
              <SearchInput
                label={journeyListConst.lbl_search}
                type={journeyListConst.lbl_search}
                placeholder={String(translate('journey_list_component.lbl_search'))}
                onChange={searchHandler}
                data-testid="filterSearchInput"
                value={searchTemp}
              />
            </div>

            <div className={`${classes.buttons} ci-button-group`}>
              {!selectedJourneyList.length ? (CIActionGuard([requiredAuthPermission.JD.edit, requiredAuthPermission.JD.approve], permissionObject.permission) && (
                <Button
                  variant="secondary"
                  onClick={importJourney}
                  data-testid="importJourneyConfiguration"
                >
                  {translate(
                    'journey_list_component.btn_import_journey_configuration'
                  )}
                </Button>)
              ) : (
                <Button
                  variant="secondary"
                  onClick={exportJourneyConfigurationHandler}
                  data-testid="exportJourneyConfiguration"
                >
                  <i className="bi bi-download" />
                  {translate(
                    'journey_list_component.btn_export_journey_configuration'
                  )}
                </Button>
              )}
              {CIActionGuard([requiredAuthPermission.JD.approve, requiredAuthPermission.JD.edit], permissionObject.permission) && (
                <Button
                  variant="primary"
                  onClick={createJourney}
                  data-testid="createNewJourney"
                  className={classes['create-jrny-btn']}
                >
                  {translate('journey_list_component.btn_create_new_journey')}
                </Button>)}
            </div>
          </div>
          <CIAppliedListFilter
            className={classes['applied-filters']}
            totalRecords={listLength}
            appliedFilters={appliedFilterData}
            onFilterChange={appliedFilterChangeHandler}
            onResetFilters={resetFilterHandler}
            showResetFiltersButton={!isShowingDefaultFilter}
          />
        </div>
        <Loader show={apiResponseStatus === DataLoadingState.LOADING} />
        {journeyList.length > 0 && apiResponseStatus !== DataLoadingState.FAILED && (
          <ListJourneys
            statusSort={statusSort}
            setStatusSort={setStatusSort}
            lastModifiedSort={lastModifiedSort}
            setLastModifiedSort={setLastModifiedSort}
            updatedBySort={updatedBySort}
            setUpdatedBySort={setUpdatedBySort}
            sortedName={sortedName}
            setSortedName={setSortedName}
            journeyNameSort={journeyNameSort}
            setJourneyNameSort={setJourneyNameSort}
            selectedJourneyList={selectedJourneyList}
            handleClick={handleClick}
            sortedOrder={sortedOrder}
            setSortedOrder={setSortedOrder}
            setSortParameter={setSortParameter}
            queryParams={queryParams}
            setQueryParams={setQueryParams}
            handleSelectAll={handleSelectAll}
            isCheckAllJourneys={isCheckAllJourneys}
            footerObj={footerObj}
            journeyList={journeyList}
            viewJourney={viewJourney}
            editJourney={editJourney}
            onDeleteSuccess={onDeleteSuccess}
          />
        )}
        {apiResponseStatus === DataLoadingState.COMPLETED
          && journeyList.length === 0 && (
            <div
              className={classes['error-container']}
              data-testid="journeyListError"
            >
              {resError !== false ? <UnableToFetch /> : <NoDataAvailable />}
            </div>
          )}
        {apiResponseStatus === DataLoadingState.FAILED && (
          <div
            className={classes['error-container']}
            data-testid="journeyListError"
          >
            {resStatusCode === 401 ? <Unathourized /> : <UnableToFetch />}
          </div>
        )}
        <Modal
          modalTitle={translate('journey_list_component.lbl_new_version')}
          btnCloseText={translate('journey_list_component.btn_text_cancel') ?? 'Cancel'}
          btnSaveText={translate('journey_list_component.btn_text_proceed') ?? 'Yes, Proceed'}
          modalBody={newVersionModalBody}
          modalShow={showVersionModal}
          onCloseModal={handleCloseVersionModal}
          onSaveModal={handleSaveVersionModal}
          alignToCentre
          disableSaveBtn={false}
          disableCloseBtn={false}
          type="warning"
        />
        <Modal
          modalTitle={translate('journey_list_component.title_toast_error_for_active_edit')}
          modalBody={translate('journey_list_component.msg_if_editing_not_allowed')}
          modalShow={showModalNotAllowed}
          btnCloseText={translate('journey_list_component.btn_text_cancel') ?? 'Cancel'}
          onCloseModal={() => setShowModalNotAllowed(false)}
          disableCloseBtn={false}
          alignToCentre
          type="warning"
        />
      </div>
      <Routes>
        <Route path="/upload-files" element={<ImportJourney />} />
      </Routes>
    </div>
  );
};

export default JourneyList;
